import { motion } from "framer-motion";
import { X } from "lucide-react";
import { ConfigProvider, DatePicker } from "antd";
import dayjs from "dayjs";
import React, { useEffect, useMemo, useState } from "react";

import {
  Info,
  Plus,
  Pencil,
  Trash2,
  Download,
  Calendar,
  ArrowLeftRight,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import ProgressRing from "../components/ui/progressRing";
import InfoTooltip from "../components/ui/InfoTooltip";
import { useTheme } from "../context/ThemeContext";
import { useDistrict } from "../utils/DistrictContext";

const Reports = ({
  dashboardWeekly,
  setDashboardWeekly,
  dashboardMonthly,
  setDashboardMonthly,
  auditMonthly,
  setAuditMonthly,
  auditYearly,
  setAuditYearly,
  dayOfMonth,
  setDayOfMonth,
  meterAlerts,
  setShowAddMeterModal,
  setEditingMeter,
  // setShowEditModal,
  setDeletingMeter,
  setShowDeleteModal,
  showDeleteModal,
  showAddMeterModal,
  selectedMeter,
  setSelectedMeter,
  selectedEndDate,
  setSelectedEndDate,
  mailAlert,
  setMailAlert,
  availableMeters,
  metersLoading,
  metersError,
  onToggleNotification,
  onAddMeter,
  onDeleteMeter,
  showEditModal,
  setShowEditModal,
  editingMeter,
  editEndDate,
  setEditEndDate,
  onUpdateMeter,

  startMonth,
  setStartMonth,
  endMonth,
  setEndMonth,
  dates,
  setDates,
  progress,
  onDownloadWaterReport,
  csvLoading,
}) => {
  const { colors } = useTheme();
  // ✅ Hooks MUST be inside the component
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [meterSearch, setMeterSearch] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { selectedDistrict } = useDistrict();

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };
  // Reset Add Meter form when modal visibility changes
  useEffect(() => {
    if (showAddMeterModal) {
      // Optional: Actions when modal opens
      setMeterSearch("");
      setSelectedMeter("");
      setIsDropdownOpen(false);
    } else {
      // Clear data when modal closes
      setMeterSearch("");
      setSelectedMeter("");
      setSelectedEndDate(null); // Clears the date too
      setIsDropdownOpen(false);
    }
  }, [showAddMeterModal, setSelectedMeter, setSelectedEndDate]);
  const filteredMeters = useMemo(() => {
    if (!meterSearch) return availableMeters;
    return availableMeters?.filter(
      (m) =>
        m.meter_id
          ?.toString()
          .toLowerCase()
          .includes(meterSearch.toLowerCase()) ||
        m.account_name?.toLowerCase().includes(meterSearch.toLowerCase()),
    );
  }, [availableMeters, meterSearch]);
  const sortedAlerts = [...meterAlerts].sort((a, b) => {
    if (!sortColumn) return 0;

    const valA = a[sortColumn] ?? "";
    const valB = b[sortColumn] ?? "";

    if (sortDirection === "asc") {
      return valA > valB ? 1 : -1;
    }

    return valA < valB ? 1 : -1;
  });
  return (
    <motion.div
      className="space-y-6 mb-8"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="space-y-6">
        {/* Reports Section */}
        <div
          className=" backdrop-blur-sm rounded-xl border p-6"
          style={{
            background: colors.cardBg,
            borderColor: colors.border,
          }}
        >
          <h2
            className="text-xl font-semibold mb-6"
            style={{ color: colors.primary }}
          >
            Reports
          </h2>

          <div className="grid grid-cols-2 gap-8 max-[567px]:grid-cols-1">
            {/* Dashboard Report */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <span className="font-medium" style={{ color: colors.text }}>
                  Dashboard Report
                </span>
                <InfoTooltip text="Enable to receive Dashboard Reports showing summary information exactly as presented on the dashboard." />
              </div>
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={dashboardWeekly}
                    // onChange={(e) => setDashboardWeekly(e.target.checked)}
                    onChange={(e) => {
                      const val = e.target.checked;
                      setDashboardWeekly(val);
                      onToggleNotification("dashboard_weekly", val);
                    }}
                    className="w-4 h-4 rounded bg-[#0a1628]  focus:ring-[#00b8f1] focus:ring-offset-0"
                    style={{
                      color: colors.primary,
                      borderColor: colors.border,
                    }}
                  />
                  <span
                    className="text-sm"
                    style={{ color: colors.textSecondary }}
                  >
                    Weekly
                  </span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={dashboardMonthly}
                    // onChange={(e) => setDashboardMonthly(e.target.checked)}
                    onChange={(e) => {
                      const val = e.target.checked;
                      setDashboardMonthly(val);
                      onToggleNotification("dashboard_monthly", val);
                    }}
                    className="w-4 h-4 rounded bg-[#0a1628]  focus:ring-[#00b8f1] focus:ring-offset-0"
                    style={{
                      color: colors.primary,
                      borderColor: colors.border,
                    }}
                  />
                  <span
                    className="text-sm"
                    style={{ color: colors.textSecondary }}
                  >
                    Monthly
                  </span>
                </label>
              </div>
            </div>

            {/* Audit Report */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <span className="font-medium" style={{ color: colors.text }}>
                  Audit Report
                </span>
                <InfoTooltip text="Enable to receive Audit Reports with detailed data in a comprehensive report format." />
              </div>
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={auditMonthly}
                    // onChange={(e) => setAuditMonthly(e.target.checked)}
                    onChange={(e) => {
                      const val = e.target.checked;
                      setAuditMonthly(val);
                      onToggleNotification("audit_report_monthly", val);
                    }}
                    className="w-4 h-4 rounded bg-[#0a1628]  focus:ring-[#00b8f1] focus:ring-offset-0"
                    style={{
                      color: colors.primary,
                      borderColor: colors.border,
                    }}
                  />
                  <span
                    className="text-sm"
                    style={{ color: colors.textSecondary }}
                  >
                    Monthly
                  </span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={auditYearly}
                    // onChange={(e) => setAuditYearly(e.target.checked)}
                    onChange={(e) => {
                      const val = e.target.checked;
                      setAuditYearly(val);
                      onToggleNotification("audit_report_yearly", val);
                    }}
                    className="w-4 h-4 rounded bg-[#0a1628]  focus:ring-[#00b8f1] focus:ring-offset-0"
                    style={{
                      color: colors.primary,
                      borderColor: colors.border,
                    }}
                  />
                  <span
                    className="text-sm"
                    style={{ color: colors.textSecondary }}
                  >
                    Yearly
                  </span>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Meter Alerts Section */}
        <div
          className=" backdrop-blur-sm rounded-xl border p-6"
          style={{
            background: colors.cardBg,
            borderColor: colors.border,
          }}
        >
          <div className="flex items-center justify-between mb-6">
            <h2
              className="text-xl font-semibold"
              style={{ color: colors.primary }}
            >
              Meter Alerts
            </h2>
            <button
              onClick={() => setShowAddMeterModal(true)}
              className="flex items-center gap-2 px-5 py-2.5  hover:from-[#00b8f1]/90 hover:to-[#0060f1]/90 rounded-lg transition-all font-medium shadow-lg shadow-[#00b8f1]/20"
              style={{
                background: colors.background,
                color: colors.text,
              }}
            >
              <Plus className="w-4 h-4" />
              <span>Add Meter</span>
            </button>
          </div>

          {/* Mail Alerts */}
          <div className="mb-6">
            <h3 className="font-medium mb-4" style={{ color: colors.text }}>
              Mail Alerts
            </h3>
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="mailAlert"
                  value="daily"
                  checked={mailAlert === "daily"}
                  // onChange={() => setMailAlert("daily")}
                  onChange={() => {
                    setMailAlert("daily");
                    onToggleNotification("notification_interval", "daily");
                  }}
                  className="w-4 h-4 border-[#2a2520] bg-[#0a1628] focus:ring-[#00b8f1] focus:ring-offset-0"
                  style={{ color: colors.primary }}
                />
                <span
                  className="text-sm"
                  style={{ color: colors.textSecondary }}
                >
                  Daily Mail
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="mailAlert"
                  value="weekly"
                  checked={mailAlert === "weekly"}
                  // onChange={() => setMailAlert("weekly")}
                  onChange={() => {
                    setMailAlert("weekly");
                    onToggleNotification("notification_interval", "weekly");
                  }}
                  className="w-4 h-4 border-[#2a2520] bg-[#0a1628]  focus:ring-[#00b8f1] focus:ring-offset-0"
                  style={{ color: colors.primary }}
                />
                <span
                  className="text-sm"
                  style={{ color: colors.textSecondary }}
                >
                  Weekly Mail
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="mailAlert"
                  value="None"
                  checked={mailAlert === "None"}
                  // onChange={() => setMailAlert("none")}
                  onChange={() => {
                    setMailAlert("None");
                    onToggleNotification("notification_interval", "None");
                  }}
                  className="w-4 h-4 border-[#2a2520] bg-[#0a1628] focus:ring-[#00b8f1] focus:ring-offset-0"
                  style={{ color: colors.primary }}
                />
                <span
                  className="text-sm"
                  style={{ color: colors.textSecondary }}
                >
                  No Mail
                </span>
              </label>
            </div>
          </div>

          {/* Meter Alerts Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                {/* <tr className="border-b" style={{ borderColor: colors.border }}>
                  <th
                    className="text-left py-3 px-4 font-semibold text-sm"
                    style={{ color: colors.textSecondary }}
                  >
                    Meter
                  </th>
                  <th
                    className="text-left py-3 px-4 font-semibold text-sm"
                    style={{ color: colors.textSecondary }}
                  >
                    Account Name
                  </th>
                  <th
                    className="text-left py-3 px-4 font-semibold text-sm"
                    style={{ color: colors.textSecondary }}
                  >
                    End Date
                  </th>
                  <th
                    className="text-center py-3 px-4  font-semibold text-sm"
                    style={{ color: colors.textSecondary }}
                  >
                    Edit
                  </th>
                  <th
                    className="text-center py-3 px-4  font-semibold text-sm"
                    style={{ color: colors.textSecondary }}
                  >
                    Delete
                  </th>
                </tr> */}
                <tr className="border-b" style={{ borderColor: colors.border }}>
                  {[
                    { key: "meter", label: "Meter" },
                    { key: "accountName", label: "Account Name" },
                    { key: "endDate", label: "End Date" },
                  ].map((col) => (
                    <th
                      key={col.key}
                      onClick={() => handleSort(col.key)}
                      className="text-left py-3 px-4 font-semibold text-sm cursor-pointer"
                      style={{ color: colors.textSecondary }}
                    >
                      <div className="flex items-center gap-2">
                        {col.label}

                        {sortColumn === col.key ? (
                          sortDirection === "asc" ? (
                            <ArrowUp
                              className="w-4 h-4"
                              style={{ color: colors.primary }}
                            />
                          ) : (
                            <ArrowDown
                              className="w-4 h-4"
                              style={{ color: colors.primary }}
                            />
                          )
                        ) : (
                          <ArrowUpDown
                            className="w-4 h-4"
                            style={{ color: colors.primary }}
                          />
                        )}
                      </div>
                    </th>
                  ))}

                  <th
                    className="text-center py-3 px-4 font-semibold text-sm"
                    style={{ color: colors.textSecondary }}
                  >
                    Edit
                  </th>

                  <th
                    className="text-center py-3 px-4 font-semibold text-sm"
                    style={{ color: colors.textSecondary }}
                  >
                    Delete
                  </th>
                </tr>
              </thead>
              <tbody>
                {/* {meterAlerts.map((alert, idx) => ( */}
                {sortedAlerts.map((alert, idx) => (
                  <tr
                    key={idx}
                    className="border-b  transition-colors"
                    style={{ borderColor: colors.borderColor }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = colors.hoverBg)
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = "transparent")
                    }
                  >
                    <td
                      className="py-3 px-4 text-sm"
                      style={{ color: colors.text }}
                    >
                      {alert.meter}
                    </td>
                    <td
                      className="py-3 px-4 text-sm"
                      style={{ color: colors.text }}
                    >
                      {alert.accountName}
                    </td>
                    <td
                      className="py-3 px-4 text-sm"
                      style={{ color: colors.text }}
                    >
                      {alert.endDate}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <button
                        className=" hover:text-[#00b8f1]/80 transition-colors"
                        style={{ color: colors.primary }}
                        onClick={() => {
                          setEditingMeter(alert);
                          setShowEditModal(true);
                        }}
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <button
                        className="text-red-400 hover:text-red-300 transition-colors"
                        onClick={() => {
                          setDeletingMeter(alert);
                          setShowDeleteModal(true);
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Water Usage Report Section */}
        <div
          className=" backdrop-blur-sm rounded-xl border p-6"
          style={{
            borderColor: colors.border,
            background: colors.cardBg,
          }}
        >
          <h2
            className="text-xl font-semibold mb-6"
            style={{ color: colors.primary }}
          >
            Water Usage Report
          </h2>

          <div className="flex flex-wrap items-end gap-4">
            {/* Date Range */}
            <div className="flex-1 min-w-[260px]">
              <label
                className="block  text-sm mb-2"
                style={{ color: colors.textSecondary }}
              >
                Date Range
              </label>

              <div
                className="relative rounded-lg px-2 py-1 transition-colors cust-border"
                style={{
                  background: colors.inputBg,
                  border: `1px solid ${colors.border}`,
                }}
              >
                <ConfigProvider
                  theme={{
                    token: {
                      colorPrimary: colors.primary,
                      colorText: colors.text,
                      colorTextSecondary: colors.textSecondary,
                      colorBorder: colors.border,
                      colorBgContainer: colors.inputBg,
                    },
                    components: {
                      DatePicker: {
                        colorBgContainer: colors.cardBg, // main popup bg
                        colorBgElevated: colors.cardBg, // dropdown layer bg
                        colorText: colors.text,
                        colorTextHeading: colors.text,
                        colorTextDisabled: colors.textSecondary,
                        cellHoverBg: colors.hoverBg,
                        cellActiveWithRangeBg: colors.primary + "20",
                        cellRangeBorderColor: colors.primary,
                        activeBorderColor: colors.primary,
                        hoverBorderColor: colors.primary,
                      },
                    },
                  }}
                >
                  <DatePicker.RangePicker
                    picker="month"
                    value={dates}
                    format="MMM YYYY"
                    disabledDate={(current) => {
                      return current && current > dayjs().endOf("month");
                    }}
                    onChange={(val, dateString) => {
                      setDates(val);
                      setStartMonth(dateString?.[0] || "");
                      setEndMonth(dateString?.[1] || "");
                    }}
                    suffixIcon={
                      <Calendar
                        style={{ color: colors.textSecondary }}
                        size={16}
                      />
                    }
                    className="w-full"
                  />
                </ConfigProvider>

                {/* Center swap icon INSIDE input */}
                <div
                  className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 px-2"
                  style={{
                    background: colors.inputBg,
                    color: colors.textSecondary,
                  }}
                >
                  <ArrowLeftRight size={16} />
                </div>
              </div>
            </div>

            {/* Day of Month */}
            <div className="w-48">
              <label
                className="block  text-sm mb-2"
                style={{ color: colors.textSecondary }}
              >
                Day of Month
              </label>
              <select
                value={dayOfMonth}
                onChange={(e) => setDayOfMonth(e.target.value)}
                className="w-full px-4 py-2 rounded-lg focus:outline-none transition-colors"
                style={{
                  color: colors.text,
                  background: colors.inputBg,
                  border: `1px solid ${colors.border}`,
                }}
              >
                {[...Array(28)].map((_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {i + 1}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-3">
              {csvLoading && <ProgressRing progress={progress} />}

              <button
                onClick={onDownloadWaterReport}
                disabled={!startMonth || !endMonth || csvLoading}
                className="flex items-center gap-2 px-6 py-2 rounded-lg transition-all disabled:opacity-50"
                style={{
                  background: colors.primary,
                  border: `1px solid ${colors.border}`,
                  color: colors.text,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = colors.primary;
                  e.currentTarget.style.background = colors.hoverBg;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = colors.border;
                  e.currentTarget.style.background = colors.cardBg;
                }}
              >
                <Download className="w-4 h-4" />
                <span className="font-medium">
                  {csvLoading ? "Preparing..." : "Download"}
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {showAddMeterModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="fixed inset-0  backdrop-blur-sm"
            style={{
              background: colors.modalOverlay,
            }}
            onClick={() => setShowAddMeterModal(false)}
          />
          <motion.div
            className=" rounded-xl border w-full max-w-lg z-10 overflow-hidden"
            style={{
              background: colors.inputBg,
              border: `1px solid ${colors.border}`,
            }}
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
          >
            <div
              className="flex items-center justify-between p-6"
              style={{ borderBottom: `1px solid ${colors.border}` }}
            >
              <h2 className="text-2xl font-bold" style={{ color: colors.text }}>
                Add Meter
              </h2>
              <button onClick={() => setShowAddMeterModal(false)}>
                <X
                  className="w-6 h-6"
                  style={{ color: colors.textSecondary }}
                />
              </button>
            </div>
            <div className="p-6 space-y-5">
              <div>
                <label
                  className="block text-sm font-semibold"
                  style={{ color: colors.primary }}
                >
                  Meter
                </label>

                <div className="relative">
                  <input
                    type="text"
                    placeholder={
                      selectedDistrict === "GPMD"
                        ? "Search Meter ID or Account Name"
                        : "Search Meter ID"
                    }
                    value={meterSearch}
                    onFocus={() => setIsDropdownOpen(true)}
                    onChange={(e) => {
                      setMeterSearch(e.target.value);
                      setIsDropdownOpen(true);
                    }}
                    className="w-full px-4 py-2.5 pr-10 rounded-lg focus:outline-none transition-all"
                    style={{
                      color: colors.text,
                      background: colors.inputBg,
                      border: `1px solid ${colors.border}`,
                    }}
                  />

                  {/* CLOSE / CLEAR BUTTON */}
                  {meterSearch && (
                    <button
                      onClick={() => {
                        setMeterSearch("");
                        setSelectedMeter("");
                        setIsDropdownOpen(false);
                      }}
                      className="absolute right-3 top-1/2 -translate-y-1/2 hover:opacity-80 transition-opacity"
                      type="button"
                    >
                      <X size={16} style={{ color: colors.textSecondary }} />
                    </button>
                  )}

                  {/* SEARCH DROPDOWN */}
                  {isDropdownOpen && (
                    <div
                      className="absolute left-0 right-0 mt-1 max-h-60 overflow-y-auto rounded-lg shadow-2xl border"
                      style={{
                        background: "#111827", // Enforced solid background to fix opacity issue
                        backgroundColor: "#111827",
                        borderColor: colors.border,
                        zIndex: 9999, // High z-index to sit on top of everything
                      }}
                    >
                      {filteredMeters?.length > 0 ? (
                        filteredMeters.map((m, idx) => (
                          <div
                            key={`${m.meter_id}-${idx}`}
                            className="px-4 py-3 cursor-pointer text-sm transition-colors border-b last:border-b-0"
                            style={{
                              color: colors.text,
                              borderColor: `${colors.border}40`,
                              position: "relative",
                              zIndex: 10000,
                            }}
                            onMouseEnter={(e) =>
                              (e.target.style.background = colors.hoverBg)
                            }
                            onMouseLeave={(e) =>
                              (e.target.style.background = "transparent")
                            }
                            onClick={() => {
                              setSelectedMeter(
                                `${m.meter_id}-${m.register_number || ""}`,
                              );
                              setMeterSearch(
                                `${m.meter_id} - ${m.account_name}`,
                              );
                              setIsDropdownOpen(false);
                            }}
                          >
                            <div
                              className="font-bold"
                              style={{ color: colors.primary }}
                            >
                              {m.meter_id}
                            </div>
                            <div className="text-xs opacity-70">
                              {m.account_name}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div
                          className="p-4 text-center text-xs"
                          style={{ color: colors.textSecondary }}
                        >
                          No meters found
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Selection Badge - Confirms what is currently active */}
                {selectedMeter && !isDropdownOpen && (
                  <div
                    className="mt-2 text-[11px] font-bold px-2 py-1 rounded inline-flex items-center gap-2"
                    style={{
                      background: colors.primary + "15",
                      color: colors.primary,
                    }}
                  >
                    <div
                      className="w-1.5 h-1.5 rounded-full"
                      style={{ background: colors.primary }}
                    />
                    Selected ID: {selectedMeter}
                  </div>
                )}

                {metersError && (
                  <p className="text-sm mt-2" style={{ color: colors.danger }}>
                    {metersError}
                  </p>
                )}
              </div>
              <div>
                <label
                  className="block text-sm font-semibold"
                  style={{ color: colors.primary }}
                >
                  End Date
                </label>
                <div
                  className=" rounded-md px-1 py-1 hover:border-[#00b8f1]/50"
                  style={{
                    background: colors.cardBg,
                    border: `1px solid ${colors.border}`,
                    color: colors.text,
                  }}
                >
                  <ConfigProvider
                    theme={{
                      token: {
                        colorPrimary: colors.primary,
                        colorText: colors.text,
                        colorTextSecondary: colors.textSecondary,
                        colorBorder: colors.border,
                        colorBgContainer: colors.inputBg,
                      },
                      components: {
                        DatePicker: {
                          colorBgContainer: colors.background, // main popup bg
                          colorBgElevated: colors.cardBg, // dropdown layer bg
                          colorText: colors.text,
                          colorTextHeading: colors.text,
                          colorTextDisabled: colors.textSecondary,
                          cellHoverBg: colors.hoverBg,
                          cellActiveWithRangeBg: colors.primary + "20",
                          cellRangeBorderColor: colors.primary,
                          activeBorderColor: colors.primary,
                          hoverBorderColor: colors.primary,
                        },
                      },
                    }}
                  >
                    <DatePicker
                      picker="date"
                      value={selectedEndDate ? dayjs(selectedEndDate) : null}
                      onChange={(date) => {
                        if (!date) return;
                        setSelectedEndDate(date.format("YYYY-MM-DD"));
                      }}
                      placeholder="Select date"
                      disabledDate={(current) =>
                        current && current < dayjs().startOf("day")
                      }
                      allowClear={false}
                      suffixIcon={
                        <Calendar
                          size={16}
                          style={{ color: colors.textSecondary }}
                        />
                      }
                      className="w-full"
                    />
                  </ConfigProvider>
                </div>
              </div>
            </div>
            <div
              className="flex items-center justify-end gap-3 p-6"
              style={{ borderTop: `1px solid ${colors.border}` }}
            >
              <button
                onClick={() => setShowAddMeterModal(false)}
                className="px-6 py-2.5 rounded-lg"
                style={{ color: colors.textSecondary }}
              >
                Cancel
              </button>
              <button
                onClick={onAddMeter}
                className="px-6 py-2.5 rounded-lg"
                style={{
                  background: colors.primary,
                  color: "#ffffff",
                }}
              >
                Add
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {showEditModal && editingMeter && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="fixed inset-0  backdrop-blur-sm"
            style={{
              background: colors.modalOverlay,
            }}
            onClick={() => setShowEditModal(false)}
          />
          <motion.div
            className=" rounded-xl border w-full max-w-lg z-10 overflow-hidden"
            style={{
              background: colors.cardBg,
              border: `1px solid ${colors.border}`,
            }}
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
          >
            <div
              className="flex items-center justify-between p-6"
              style={{ borderBottom: `1px solid ${colors.border}` }}
            >
              <h2 className="text-2xl font-bold" style={{ color: colors.text }}>
                Edit Meter
              </h2>
              <button onClick={() => setShowEditModal(false)}>
                <X
                  className="w-6 h-6"
                  style={{ color: colors.textSecondary }}
                />
              </button>
            </div>

            <div className="p-6 space-y-5">
              {/* Meter (read-only) */}
              <div>
                <label
                  className="block text-sm font-semibold"
                  style={{ color: colors.primary }}
                >
                  Meter
                </label>
                <input
                  value={`${editingMeter.meter_id}${editingMeter.register_number ? ` (${editingMeter.register_number})` : ""}`}
                  disabled
                  className="w-full px-4 py-2.5 rounded-lg opacity-80 cursor-not-allowed"
                  style={{
                    color: colors.text,
                    background: colors.inputBg,
                    borderColor: colors.border,
                  }}
                />
              </div>

              {/* Account Name (read-only) */}
              <div>
                <label
                  className="block text-sm font-semibold"
                  style={{ color: colors.primary }}
                >
                  Account Name
                </label>
                <input
                  value={editingMeter.accountName || ""}
                  disabled
                  className="w-full px-4 py-2.5 rounded-lg opacity-80 cursor-not-allowed"
                  style={{
                    color: colors.text,
                    background: colors.inputBg,
                    borderColor: colors.border,
                  }}
                />
              </div>

              {/* End Date (editable) */}
              <div>
                <label
                  className="block text-sm font-semibold"
                  style={{ color: colors.primary }}
                >
                  End Date
                </label>
                <div
                  className=" border  rounded-md px-1 py-1 hover:border-[#00b8f1]/50"
                  style={{
                    background: colors.cardBg,
                    border: `1px solid ${colors.border}`,
                  }}
                >
                  <ConfigProvider
                    theme={{
                      token: {
                        colorPrimary: colors.primary,
                        colorText: colors.text,
                        colorTextSecondary: colors.textSecondary,
                        colorBorder: colors.border,
                        colorBgContainer: colors.inputBg,
                      },
                      components: {
                        DatePicker: {
                          colorBgContainer: colors.inputBg, // main popup bg
                          colorBgElevated: colors.cardBg, // dropdown layer bg
                          colorText: colors.text,
                          colorTextHeading: colors.text,
                          colorTextDisabled: colors.textSecondary,
                          cellHoverBg: colors.hoverBg,
                          cellActiveWithRangeBg: colors.primary + "20",
                          cellRangeBorderColor: colors.primary,
                          activeBorderColor: colors.primary,
                          hoverBorderColor: colors.primary,
                        },
                      },
                    }}
                  >
                    <DatePicker
                      picker="date"
                      value={editEndDate ? dayjs(editEndDate) : null}
                      onChange={(date) => {
                        if (!date) return;
                        setEditEndDate(date.format("YYYY-MM-DD"));
                      }}
                      placeholder="Select date"
                      disabledDate={(current) =>
                        current && current < dayjs().startOf("day")
                      }
                      allowClear={false}
                      suffixIcon={
                        <Calendar
                          size={16}
                          style={{ color: colors.textSecondary }}
                        />
                      }
                      className="w-full"
                    />
                  </ConfigProvider>
                </div>
              </div>
            </div>

            <div
              className="flex items-center justify-end gap-3 p-6"
              style={{ borderTop: `1px solid ${colors.border}` }}
            >
              <button
                onClick={() => setShowEditModal(false)}
                className="px-6 py-2.5"
                style={{ color: colors.textSecondary }}
              >
                Cancel
              </button>
              <button
                onClick={onUpdateMeter}
                className="px-6 py-2.5 rounded-lg"
                style={{
                  background: colors.primary,
                  color: "#ffffff",
                }}
              >
                Update
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className=" rounded-xl w-[420px] p-6 relative shadow-2xl border "
            style={{
              background: colors.cardBg,
              borderColor: colors.border,
            }}
          >
            {/* Close */}
            <button
              onClick={() => setShowDeleteModal(false)}
              className="absolute top-3 right-3 hover:text-white"
              style={{ color: colors.textSecondary }}
            >
              ✕
            </button>

            <h3
              className="text-lg font-semibold  mb-6"
              style={{ color: colors.text }}
            >
              Are you sure you want to permanently remove this item?
            </h3>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 rounded-lg bg-[#1a2a40] hover:bg-[#243a55]"
                style={{
                  color: colors.textSecondary,
                  background: colors.background,
                }}
              >
                Cancel
              </button>

              <button
                onClick={onDeleteMeter}
                className="px-4 py-2 rounded-lg text-white"
                style={{
                  background: colors.primary,
                }}
              >
                OK
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </motion.div>
  );
};

export default Reports;
